import { DocumentModelMetadata } from 'types/dynamic-forms/DocumentModelMetadata';
import { DataSources } from 'types/dynamic-forms/DataSources';
import { DocumentProperty } from 'types/dynamic-forms/DocumentProperty';
import { DocumentPropertyBoolRule } from 'types/dynamic-forms/DocumentPropertyBoolRule';
import { DocumentPropertyRef } from 'types/dynamic-forms/DocumentPropertyRef';
import { formatOptionLabelWithUnit } from 'types/dynamic-forms/formatOptionLabelWithUnit';
import { NOT_ASSIGNED } from 'constants/constants';
import { formatDate } from 'util/utils';
import {
  VersionLogFormat,
  VersionLogRow,
  VersionLogReferenceFlowRow,
  VersionLogEnumRow,
  VersionLogListRow,
  VersionLogMaterialPropertiesRow,
  VersionLogUnitValueRow,
  VersionLogDatabasesRow,
} from './records/VersionLogRow';

export interface ResolvedRowValue {
  name: string;
  value: string;
  isHtmlRichText?: boolean;
  hasValue: boolean;
  /** Document paths contributing to this row, used to aggregate the backend property diff. */
  propertyPaths: string[];
}

type FormattedValue = { value: string; isHtmlRichText?: boolean };

type ValueFormatter = (
  raw: unknown,
  row: VersionLogRow,
  dataSources: DataSources,
  properties?: DocumentProperty[]
) => FormattedValue;

const getValueByPath = (source: unknown, path: string): unknown =>
  path.split('.').reduce<any>((node, key) => (node == null ? undefined : node[key]), source);

const findPropertyByPath = (properties: DocumentProperty[], path: string): DocumentProperty | undefined => {
  let currentProperties = properties;
  let property: DocumentProperty | undefined;

  for (const segment of path.split('.')) {
    property = currentProperties.find((item) => item.propertyId === segment);
    if (!property) {
      return undefined;
    }
    currentProperties = property.children ?? [];
  }

  return property;
};

const isEmptyValue = (raw: unknown): boolean =>
  raw === undefined ||
  raw === null ||
  (typeof raw === 'string' && raw.trim().length === 0) ||
  (Array.isArray(raw) && raw.length === 0);

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const findOption = (dataSources: DataSources, dataSourceId: string | undefined, value: unknown) => {
  const options = dataSourceId ? dataSources[dataSourceId] : undefined;
  return options?.find((o) => String(o.value) === String(value));
};

const resolveOptionLabel = (dataSources: DataSources, dataSourceId: string | undefined, value: unknown): string => {
  if (isEmptyValue(value)) {
    return '';
  }
  const option = findOption(dataSources, dataSourceId, value);
  return option?.label ?? String(value);
};

/** Resolves a unit-bearing option (label + optional `meta.unit`) as rich text, e.g. for material-property units. */
const resolveOptionLabelWithUnit = (dataSources: DataSources, dataSourceId: string | undefined, value: unknown) =>
  formatOptionLabelWithUnit(findOption(dataSources, dataSourceId, value), isEmptyValue(value) ? '' : String(value));

const toYesNo = (raw: unknown): string => {
  if (raw === true || raw === 'Yes') {
    return 'Yes';
  }
  if (raw === false || raw === 'No') {
    return 'No';
  }
  return String(raw);
};

const applyTemplate = (template: string, value: string): string => template.replace('{value}', value);

const formatList = (
  raw: unknown,
  row: VersionLogListRow,
  dataSources: DataSources,
  properties: DocumentProperty[] = []
): string => {
  const items = Array.isArray(raw) ? raw : [raw];
  const itemProperties = findPropertyByPath(properties, row.propertyId)?.children;
  const joined = items
    .map((item) => {
      if (row.itemLabelPath) {
        return String(getDocumentValue(item, row.itemLabelPath, itemProperties) ?? '');
      }
      const lookupValue = row.itemValuePath ? getDocumentValue(item, row.itemValuePath, itemProperties) : item;
      return resolveOptionLabel(dataSources, row.dataSource, lookupValue);
    })
    .filter((label) => !isEmptyValue(label))
    .join(', ');
  return joined && row.itemTemplate ? applyTemplate(row.itemTemplate, joined) : joined;
};

const formatters: Record<
  Exclude<VersionLogFormat, 'referenceFlow' | 'materialProperties' | 'unitValue' | 'databases'>,
  ValueFormatter
> = {
  date: (raw) => ({ value: (formatDate(raw as string) as string) || '' }),
  html: (raw) => ({ value: String(raw), isHtmlRichText: true }),
  yesNo: (raw) => ({ value: toYesNo(raw) }),
  version: (raw) => ({ value: String(raw).padStart(3, '0') }),
  enum: (raw, row, dataSources) => ({ value: resolveOptionLabel(dataSources, (row as VersionLogEnumRow).dataSource, raw) }),
  list: (raw, row, dataSources, properties) => ({
    value: formatList(raw, row as VersionLogListRow, dataSources, properties),
  }),
};

/**
 * Evaluates a DocumentPropertyBoolRule against root document data.
 * Returns true when the property should be visible, false when it should be hidden.
 * Only rootProp refs are supported; other ref types default to visible.
 */
const evaluateVisibilityRule = (
  rule: DocumentPropertyBoolRule,
  refs: DocumentPropertyRef[] | undefined,
  data: Record<string, unknown>,
  selfValue?: unknown,
  properties?: DocumentProperty[]
): boolean => {
  switch (rule.__typename) {
    case 'anotherPropEquals': {
      const ref = refs?.[rule.refId];
      if (!ref || ref.refType !== 'rootProp') return true;
      const value = getDocumentValue(data, ref.propertyId, properties);
      return value === rule.value;
    }
    case 'or':
      return (
        evaluateVisibilityRule(rule.a, refs, data, selfValue, properties) ||
        evaluateVisibilityRule(rule.b, refs, data, selfValue, properties)
      );
    case 'notEmptyOptions':
    case 'anyItem':
      // These rule types depend on datasource info or item context not available
      // in this read-only renderer — default to visible.
      return true;
    case 'selfNotEmpty':
      return !isEmptyValue(selfValue);
    default:
      return true;
  }
};

/** Defaults apply only to absent properties, including nested ones. Explicit null clears a subtree. */
const getDocumentValue = (data: Record<string, unknown>, path: string, properties: DocumentProperty[] = []): unknown => {
  let value: unknown = data;
  let children = properties;
  for (const segment of path.split('.')) {
    if (value === null) return null;
    const property = children.find((item) => item.propertyId === segment);
    const stored = (value as Record<string, unknown> | undefined)?.[segment];
    value = stored === undefined ? property?.defaultValue : stored;
    children = property?.children ?? [];
  }
  return value;
};

/**
 * Resolves a single row against a document version's data/metadata into a display value
 * (label, formatted value, and whether the value is rich HTML). Returns `null` when:
 * - the property's `visibilityRule` (from properties.json) evaluates to hidden, or
 * - the value is empty and the row is marked `hideWhenEmpty`,
 * so the caller can drop the row.
 */
export const resolveRow = (
  row: VersionLogRow,
  data: Record<string, unknown>,
  metadata: DocumentModelMetadata,
  dataSources: DataSources,
  properties?: DocumentProperty[]
): ResolvedRowValue | null => {
  const propertyPaths: string[] = [];
  const readData = (path: string): unknown => {
    propertyPaths.push(path);
    return getDocumentValue(data, path, properties);
  };
  const result = (value: string, isHtmlRichText?: boolean, hasValue = !isEmptyValue(value)): ResolvedRowValue | null => {
    if (!hasValue && row.hideWhenEmpty) return null;
    return {
      name: row.label,
      value: hasValue ? value : NOT_ASSIGNED,
      isHtmlRichText,
      hasValue,
      propertyPaths,
    };
  };

  // Material-properties array row: formats each array item as "Unit: {label}\nValue: {val}",
  // joining multiple items with a blank line. Handled before the standard path because it reads
  // an array property and has no propertySource / visibilityRule.
  if (row.format === 'materialProperties') {
    const mpRow = row as VersionLogMaterialPropertiesRow;
    const unitPath = mpRow.unitPath ?? 'unit';
    const valuePath = mpRow.valuePath ?? 'value';
    const raw = readData(mpRow.propertyId);
    const items = Array.isArray(raw) ? raw : [];
    if (items.length === 0) {
      return result('');
    }
    const itemProperties = findPropertyByPath(properties ?? [], mpRow.propertyId)?.children;
    const parts = items.map((item) => {
      const unitKey = getDocumentValue(item, unitPath, itemProperties);
      const val = getDocumentValue(item, valuePath, itemProperties);
      const resolvedUnit =
        unitKey != null
          ? resolveOptionLabelWithUnit(dataSources, mpRow.dataSource, unitKey)
          : { label: '', isHtmlRichText: false };
      return { unit: resolvedUnit, value: val != null ? String(val) : '' };
    });
    // A single unit carrying inline HTML (e.g. `<sup>2</sup>`) forces the whole cell to be rendered
    // as rich text, so every other (untrusted) segment must be escaped to avoid HTML injection.
    const isHtmlRichText = parts.some((part) => part.unit.isHtmlRichText);
    const formatted = parts
      .map((part) => {
        const unitLabel = isHtmlRichText && !part.unit.isHtmlRichText ? escapeHtml(part.unit.label) : part.unit.label;
        const valueText = isHtmlRichText ? escapeHtml(part.value) : part.value;
        return `Unit: ${unitLabel}\nValue: ${valueText}`;
      })
      .join('\n\n');
    return result(
      formatted,
      isHtmlRichText,
      parts.some((part) => !isEmptyValue(part.unit.label) || !isEmptyValue(part.value))
    );
  }

  // Unit-value compound row: combines a unit enum property and a numeric value property into
  // a single cell formatted as "Unit: {label}\nValue: {value}". Handled before the standard
  // path because it reads two separate properties and has no propertySource / visibilityRule.
  if (row.format === 'unitValue') {
    const uvRow = row as VersionLogUnitValueRow;
    const val = readData(uvRow.propertyId);
    const unitKey = readData(uvRow.unitPropertyId);
    if (isEmptyValue(val) && isEmptyValue(unitKey)) {
      return result('');
    }
    const resolvedUnit =
      unitKey != null
        ? resolveOptionLabelWithUnit(dataSources, uvRow.dataSource, unitKey)
        : { label: '', isHtmlRichText: false };
    const rawValue = val != null ? String(val) : '';
    const valueText = resolvedUnit.isHtmlRichText ? escapeHtml(rawValue) : rawValue;
    const formatted = `Unit: ${resolvedUnit.label}\nValue: ${valueText}`;
    return result(formatted, resolvedUnit.isHtmlRichText);
  }

  // Databases row: reads the <propertyId>.databases array and resolves each item's version label
  // via the fixed 'lcaDatabaseVersions' data source. When <propertyId>.otherLcaDatabaseEnabled is
  // 'Yes', the free-text name at <propertyId>.otherLcaDatabaseName is appended on a new line with
  // an "Other database:" prefix, separated from the main list by a blank line.
  // Handled before the standard path because it reads an array property and has no propertySource / visibilityRule.
  if (row.format === 'databases') {
    const dbRow = row as VersionLogDatabasesRow;
    const databasesPath = `${dbRow.propertyId}.databases`;
    const otherEnabledPath = `${dbRow.propertyId}.otherLcaDatabaseEnabled`;
    const otherNamePath = `${dbRow.propertyId}.otherLcaDatabaseName`;
    const dataSourceId = 'lcaDatabaseVersions';

    const raw = readData(databasesPath);
    const items = Array.isArray(raw) ? raw : [];

    const itemProperties = findPropertyByPath(properties ?? [], databasesPath)?.children;
    const labels = items
      .map((item) => resolveOptionLabel(dataSources, dataSourceId, getDocumentValue(item, 'versionId', itemProperties)))
      .filter((label) => !isEmptyValue(label));

    let otherLabel: string | null = null;
    const otherEnabled = getDocumentValue(data, otherEnabledPath, properties);
    if (otherEnabled === 'Yes') {
      propertyPaths.push(otherEnabledPath);
      const otherName = readData(otherNamePath);
      if (!isEmptyValue(otherName)) {
        otherLabel = `Other database: ${String(otherName)}`;
      }
    }

    if (labels.length === 0 && otherLabel == null) {
      return result('');
    }

    const parts: string[] = [];
    if (labels.length > 0) {
      parts.push(labels.join('\n'));
    }
    if (otherLabel != null) {
      parts.push(otherLabel);
    }
    return result(parts.join('\n\n'));
  }

  // Compound reference-flow row: reads three separate properties and composes a single value.
  // Handled first because VersionLogReferenceFlowRow has no propertySource / visibilityRule.
  if (row.format === 'referenceFlow') {
    const name = readData(row.propertyId);
    const flowValue = readData(row.valuePropertyId);
    const flowUnit = readData(row.unitPropertyId);
    if (isEmptyValue(name) && isEmptyValue(flowValue) && isEmptyValue(flowUnit)) {
      return result('');
    }
    const unitOption = flowUnit != null ? findOption(dataSources, row.dataSource, flowUnit) : undefined;
    const unitLabel = unitOption?.label ?? (flowUnit != null ? escapeHtml(String(flowUnit)) : '');
    const valuePart = [unitLabel, flowValue != null ? escapeHtml(String(flowValue)) : ''].filter(Boolean).join(', ');
    const formatted = [escapeHtml(String(name ?? '')), valuePart].filter(Boolean).join('\n');
    return result(formatted, true);
  }

  // Respect the visibilityRule from properties.json (applies only to data rows).
  if (properties && row.propertySource !== 'metadata') {
    const property = findPropertyByPath(properties, row.propertyId);
    if (property?.visibilityRule) {
      const selfValue = getDocumentValue(data, row.propertyId, properties);
      if (!evaluateVisibilityRule(property.visibilityRule, property.refs, data, selfValue, properties)) {
        return null;
      }
    }
  }

  const raw = row.propertySource === 'metadata' ? getValueByPath(metadata, row.propertyId) : readData(row.propertyId);
  if (row.propertySource === 'metadata') propertyPaths.push(row.propertyId);

  if (isEmptyValue(raw)) {
    return result('');
  }

  const formatter = row.format ? formatters[row.format] : undefined;
  const formatted = formatter ? formatter(raw, row, dataSources, properties) : { value: String(raw) };

  return result(formatted.value, formatted.isHtmlRichText);
};
